/**
 * The pictures the site uses.
 *
 * A thin screen on purpose. The originals live in `src/images/` and the build
 * makes every size it needs from them, so there is nothing here to manage
 * beyond "what is there" and "add one". Deleting is deliberately absent: the
 * build's naming depends on a file name meaning one picture for good, and a
 * picture referenced from a body that was deleted here would be a broken image
 * with no error until somebody looked at the page.
 */
import React, { useEffect, useState } from "react";

import { ApiError, api } from "../api.js";
import { ImageField, Message } from "../components/bits.jsx";

export default function Media({ may }) {
  const [images, setImages] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const load = () =>
    api
      .get("media")
      .then((data) => setImages(data.images))
      .catch((problem) => setError(problem instanceof ApiError ? problem.message : "Could not list the pictures."));

  useEffect(() => {
    load();
  }, []);

  const shown = (images || []).filter((i) => i.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <>
      <div className="head">
        <div>
          <h1>Pictures</h1>
          <p className="sub">
            {images ? `${images.length} in src/images/.` : ""} The build makes every size it needs from these.
          </p>
        </div>
      </div>

      <Message level="error">{error}</Message>

      {may("upload") && (
        <div className="card">
          <div className="body">
            <ImageField label="Add a picture" value={null} onChange={load} help="Resized to 2400px and re-saved in your browser before it is uploaded, so the repository stays small. Location data does not survive that." />
          </div>
        </div>
      )}

      <div className="card">
        <header className="row">
          <input style={{ flex: "1 1 14rem" }} placeholder="Search by file name" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search pictures" />
        </header>
        <div className="body">
          {!images ? (
            <div className="empty">Loading…</div>
          ) : !shown.length ? (
            <div className="empty">Nothing matches.</div>
          ) : (
            <div className="grid">
              {shown.map((image) => (
                <figure key={image.name} title={`${image.name} — ${Math.round(image.size / 1024)} KB`}>
                  <img src={image.url} alt="" loading="lazy" />
                  <figcaption>{image.name}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="hint">
        Pictures are not deleted here. A file name means one picture for good — the build depends on
        that — and a picture removed while an article still points at it would be a broken image
        nobody notices. Ask the tech team if one really has to go.
      </p>
    </>
  );
}
